import React from 'react';
import PlacePhoto from '../PlacePhoto';
import DesignationIcon from '../DesignationIcon';

// Category icons for free tier (when photos are locked)
const getCategoryIcon = (category) => {
  const icons = {
    'Food & Drinks': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
        <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
        <line x1="6" y1="1" x2="6" y2="4"/>
        <line x1="10" y1="1" x2="10" y2="4"/>
        <line x1="14" y1="1" x2="14" y2="4"/>
      </svg>
    ),
    'Shopping': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
        <line x1="3" y1="6" x2="21" y2="6"/>
        <path d="M16 10a4 4 0 0 1-8 0"/>
      </svg>
    ),
    'Museums/Culture': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M3 21h18"/>
        <path d="M5 21V7l7-4 7 4v14"/>
        <path d="M9 21v-6h6v6"/>
      </svg>
    ),
    'Activities': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <circle cx="12" cy="12" r="10"/>
        <polygon points="10 8 16 12 10 16 10 8"/>
      </svg>
    ),
    'Hotels': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M3 21v-9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v9"/>
        <path d="M3 21h18"/>
        <path d="M9 10V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v4"/>
      </svg>
    ),
    'Parks/Outdoors': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M12 2L2 22h20L12 2z"/>
        <path d="M12 12v6"/>
      </svg>
    ),
    'General Places': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    ),
  };
  return icons[category] || icons['General Places'];
};

// PlacePhoto wrapper for list detail styling
const ListDetailPlacePhoto = ({ place, canShowPhotos = true }) => {
  const placeholderSvg = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );

  const loadingSvg = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  );

  // For free users, show category icon instead of photo
  if (!canShowPhotos) {
    return (
      <div className="place-photo-placeholder category-icon">
        {getCategoryIcon(place.category)}
      </div>
    );
  }

  return (
    <PlacePhoto
      placeId={place.placeId}
      placeName={place.name}
      storedPhotoUrl={place.photoUrl}
      photoUrlSavedAt={place.photoUrlSavedAt}
      className="place-photo-img"
      loadingClassName="place-photo-placeholder loading"
      errorClassName="place-photo-placeholder"
      placeholderContent={placeholderSvg}
      loadingContent={loadingSvg}
    />
  );
};

// Get badge icon for display - now returns DesignationIcon component
const getBadgeIcon = (badge) => {
  return <DesignationIcon designation={badge || 'saved'} size={14} />;
};

function PlaceCard({
  place,
  isOwnList,
  isCollaborationList,
  isUserCollaborator, // Whether the current user is a collaborator on this list
  canShowPhotos,
  canShowOpeningHours,
  openStatus,
  priceLevel,
  subcategory,
  street,
  cityState,
  userDesignation, // User's global designation for this place
  userPlacesMap,
  friendsPlacesMap,
  overflowingElements,
  elementRefs,
  onPlaceClick,
  onManagePlace,
  hoursUnknown = false, // When true, show "Hours unknown" indicator (used by Open At filter)
}) {
  // Determine which designation to display:
  // - Own list or collaboration list where user is collaborator: use user's global designation
  // - Viewing someone else's list: use owner's designation from the item
  const shouldUseOwnerDesignation = !isOwnList && !isUserCollaborator;
  const displayDesignation = shouldUseOwnerDesignation ? place.ownerDesignation : userDesignation;

  const badge = displayDesignation?.badge;
  const notes = displayDesignation?.notes;
  const avoidReason = displayDesignation?.avoidReason;

  // Handle click with text selection prevention for mobile
  const handleCardClick = (e) => {
    // Prevent text selection on mobile which can trigger Chrome's search bar
    e.preventDefault();
    window.getSelection()?.removeAllRanges();
    onPlaceClick(place);
  };

  return (
    <div className="place-card">
      <div onClick={handleCardClick}>
        <div className="place-content-wrapper">
          <div className="place-photo">
            <ListDetailPlacePhoto place={place} canShowPhotos={canShowPhotos} />
          </div>
          <div className="place-details">
            <div className="place-header">
              <div className="place-header-left">
                <div className="place-name-wrapper">
                  <h3
                    className="place-name"
                    ref={(el) => {
                      if (el) elementRefs.current[`name-${place.id}`] = el;
                    }}
                    title={overflowingElements.has(`name-${place.id}`) ? place.name : undefined}
                  >
                    {place.name}
                  </h3>
                  {canShowOpeningHours && openStatus && !hoursUnknown && (
                    <span
                      className={`open-status-indicator ${openStatus.status}`}
                      data-full-text={openStatus.tooltip}
                    >
                      {openStatus.status === 'permanently-closed' ? (
                        <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                          <line x1="4" y1="4" x2="20" y2="20" />
                          <line x1="20" y1="4" x2="4" y2="20" />
                        </svg>
                      ) : null}
                    </span>
                  )}
                  {hoursUnknown && (
                    <span
                      className="hours-unknown-indicator"
                      data-full-text="Hours unknown"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                    </span>
                  )}
                </div>
                {place.address && (
                  <div className="place-location">
                    <svg className="location-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    <div className="place-address-lines">
                      {street && (
                        <div
                          className="address-street"
                          ref={(el) => {
                            if (el) elementRefs.current[`street-${place.id}`] = el;
                          }}
                          title={overflowingElements.has(`street-${place.id}`) ? street : undefined}
                        >
                          {street}
                        </div>
                      )}
                      {cityState && (
                        <div
                          className="address-city-state"
                          ref={(el) => {
                            if (el) elementRefs.current[`cityState-${place.id}`] = el;
                          }}
                          title={overflowingElements.has(`cityState-${place.id}`) ? cityState : undefined}
                        >
                          {cityState}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="place-header-actions">
                <div className="place-badge-wrapper">
                  {notes && (
                    <span
                      className="place-note-indicator"
                      data-full-text={notes}
                    >
                      <DesignationIcon designation="notes" size={14} />
                    </span>
                  )}
                  {badge ? (
                    <span
                      className={`place-badge ${badge}`}
                      data-full-text={
                        badge === 'gem' ? 'Gem' :
                        badge === 'highly-recommended' ? 'Highly Recommended' :
                        badge === 'visited' ? 'Visited' :
                        badge === 'avoid' ? `Avoid${avoidReason ? ': ' + avoidReason.charAt(0).toUpperCase() + avoidReason.slice(1) : ''}` :
                        badge === 'want-to-go' ? 'Want to Go' : ''
                      }
                    >
                      <DesignationIcon designation={badge} size={14} />
                    </span>
                  ) : (
                    // Unrated - show gray placeholder
                    <span className="place-badge unrated" data-full-text="Unrated">
                      <DesignationIcon designation="unrated" size={14} />
                    </span>
                  )}
                </div>
                {(isOwnList || isCollaborationList) && (
                  <button
                    type="button"
                    className="btn-edit-place"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      window.getSelection()?.removeAllRanges();
                      onManagePlace(place);
                    }}
                  >
                    ⋯
                  </button>
                )}
                {!isOwnList && !isCollaborationList && place.placeId && (() => {
                  const placeInfo = userPlacesMap[place.placeId];
                  const isOnList = placeInfo && placeInfo.length > 0;
                  const listCount = placeInfo?.length || 0;
                  const userBadge = userDesignation?.badge;
                  const tooltip = isOnList
                    ? listCount === 1
                      ? `On: ${placeInfo[0].listName}`
                      : `On ${listCount} lists`
                    : undefined;

                  if (isOnList && userBadge) {
                    const badgeLabel =
                      userBadge === 'gem' ? 'Gem' :
                      userBadge === 'highly-recommended' ? 'Highly Recommended' :
                      userBadge === 'visited' ? 'Visited' :
                      userBadge === 'avoid' ? 'Avoid' :
                      userBadge === 'want-to-go' ? 'Want to Go' : 'Saved';
                    const userTooltip = `You: ${badgeLabel}`;

                    return (
                      <button
                        className="place-user-designation"
                        data-full-text={userTooltip}
                        onClick={(e) => {
                          e.stopPropagation();
                          onManagePlace(place);
                        }}
                        title={userTooltip}
                      >
                        <DesignationIcon designation={userBadge} size={14} />
                      </button>
                    );
                  }

                  return (
                    <button
                      className="place-user-designation place-user-designation-add"
                      onClick={(e) => {
                        e.stopPropagation();
                        onManagePlace(place);
                      }}
                      title="Save to list"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                  );
                })()}
              </div>
            </div>
            <div className="place-meta">
              <div className="place-category-wrapper">
                <span className="place-category-tag">{place.category || 'Other'}</span>
                {subcategory && (
                  <span className="place-subcategory-tag">{subcategory}</span>
                )}
                {priceLevel && (
                  <span className="place-price-tag">{priceLevel}</span>
                )}
                {place.placeId && friendsPlacesMap[place.placeId]?.length > 0 && (
                  <span
                    className="place-friends-tag"
                    title={`${friendsPlacesMap[place.placeId].length} friend${friendsPlacesMap[place.placeId].length > 1 ? 's' : ''} saved this`}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  </span>
                )}
              </div>
              <div className="place-stats">
                {place.distance && (
                  <span className="place-distance">{place.distance}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlaceCard;
export { getCategoryIcon, ListDetailPlacePhoto, getBadgeIcon };
